import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CampaignCreationState, 
  CampaignCreationWizardProps, 
  WizardStep,
  CampaignV2,
  LeadSource,
  LeadSourceOrNull
} from '../../types';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
// Header is provided by AuthenticatedRoute wrapper
import UnsavedChangesModal from './UnsavedChangesModal';
import { createCampaign, updateCampaign } from '../../utils/campaignApi';
import { supabase } from '../../supabase/client';
import { useCSRContext } from '../../hooks/useCSRContext';
import { CSRUserSelector } from '../csr/CSRUserSelector';
import { 
  readWizardStep, 
  writeWizardStep, 
  clearWizardStep, 
  updateWizardUrl 
} from '../../utils/wizardPersistence';
import { WizardCache, reconcileWizardState } from '../../utils/wizardCache';

// Import step components (to be created)
import BasicInformationStep from './steps/BasicInformationStep';
// EPOCH 63: ServiceTierSelectionStep removed - single tier (Platform), step skipped
// import ServiceTierSelectionStep from './steps/ServiceTierSelectionStep';
import LeadAcquisitionMethodStep from './steps/LeadAcquisitionMethodStep';
import LeadManagementStep from './steps/LeadManagementStep';
import MessageSequenceBuilderStep from './steps/MessageSequenceBuilderStep';
import ReviewAndConfirmationStep from './steps/ReviewAndConfirmationStep';

// Progress indicator component
const WizardProgress: React.FC<{ 
  currentStep: number; 
  totalSteps: number; 
  stepTitles: string[];
  isViewMode?: boolean;
  isEditMode?: boolean;
}> = ({
  currentStep,
  totalSteps,
  stepTitles,
  isViewMode = false,
  isEditMode = false
}) => {
  return (
    <div className="w-full bg-white border-b border-gray-200 px-6 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {isViewMode ? 'View Campaign' : isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
          </h1>
          <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
        
        {/* Step indicators */}
        <div className="flex justify-between">
          {stepTitles.map((title, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isCompleted = stepNumber < currentStep;
            
            return (
              <div key={stepNumber} className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    isCompleted
                      ? 'bg-green-600 text-white'
                      : isActive
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {isCompleted ? '✓' : stepNumber}
                </div>
                <span className={`mt-2 text-xs text-center max-w-20 ${
                  isActive ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}>
                  {title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CampaignCreationWizard: React.FC<CampaignCreationWizardProps> = ({
  onComplete,
  onCancel,
  initialState,
  serviceTier: initialServiceTier,
  existingCampaign,
  isViewMode = false,
  isEditMode = false
}) => {
  const navigate = useNavigate();
  const { isCSR, targetUser } = useCSRContext();
  
  // Check URL for campaign ID (for draft recovery)
  const urlParams = new URLSearchParams(window.location.search);
  const urlCampaignId = urlParams.get('edit') || urlParams.get('view');
  
  console.log('🚀 CampaignCreationWizard mounted:', {
    urlCampaignId,
    existingCampaignId: existingCampaign?.id,
    isEditMode,
    isViewMode,
    existingCampaignStatus: existingCampaign?.status
  });
  
  // Modal state for unsaved changes
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [, setPendingNavigation] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Epoch 71: Track whether this is the user's first campaign (for onboarding)
  const [isFirstCampaign, setIsFirstCampaign] = useState(false);
  
  // Initialize wizard state
  const [state, setState] = useState<CampaignCreationState>(() => {
    // Check if we're resuming a draft campaign from URL
    let initialStep = 1;
    let resumedCampaignId: string | undefined;
    
    if (urlCampaignId && !isViewMode) {
      // Try to restore step from sessionStorage immediately
      // EPOCH 63: Cap max step to 5 (was 6 before Service Tier step removal)
      initialStep = readWizardStep(urlCampaignId, 5);
      resumedCampaignId = urlCampaignId;
      console.log('🔄 Attempting early draft recovery:', {
        campaignId: urlCampaignId,
        restoredStep: initialStep
      });
    }
    
    return {
      // Default state
      campaign_name: '',
      service_tier: initialServiceTier || 'junior_sdr',
      lead_acquisition_method: null as LeadSourceOrNull, // Start with null to prevent race condition
      selected_preset_filters: [],
      selected_existing_lists: [],
      sequence_name: '',
      messages: [],
      use_existing_template: false,
      save_as_template: false,
      total_estimated_leads: 0,
      ai_chatbot_enabled: false,
      current_step: initialStep, // Use restored step if available
      validation_errors: {},
      is_loading: false,
      lead_lists_ids: [],
      preset_filter_data: undefined, // Store filter data temporarily
      campaign_id: resumedCampaignId, // Set campaign_id if resuming
      
      // Merge with any provided initial state
      ...initialState
    };
  });

  // Epoch 71: Check if this is the user's first campaign (0 non-draft campaigns)
  useEffect(() => {
    const checkFirstCampaign = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { count, error } = await supabase
          .from('campaigns')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', session.user.id)
          .neq('status', 'draft');

        if (!error) {
          setIsFirstCampaign((count ?? 0) === 0);
        }
      } catch (err) {
        console.error('Epoch 71: Error checking campaign count:', err);
        // Default to false on error - skip onboarding rather than break
      }
    };
    if (!isViewMode) {
      checkFirstCampaign();
    }
  }, [isViewMode]);

  // Save wizard state when tab visibility changes (user switches tabs)
  // EPOCH 59: Now saves full state including messages, not just step number
  useEffect(() => {
    if (!state.campaign_id) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Save step to wizardPersistence (legacy)
        writeWizardStep(state.campaign_id!, state.current_step);

        // EPOCH 59: Save FULL state to WizardCache including messages
        console.log('📱 EPOCH59: Tab hidden - saving full state to cache', {
          step: state.current_step,
          messagesCount: state.messages?.length || 0,
          sequenceName: state.sequence_name
        });
        WizardCache.write(state.campaign_id!, {
          step: state.current_step,
          leadMethod: state.lead_acquisition_method,
          campaignName: state.campaign_name,
          serviceTier: state.service_tier,
          messages: state.messages,
          sequenceName: state.sequence_name,
          campaignGoal: state.campaignGoal
        });
      }
    };

    const handleBeforeUnload = () => {
      // Save state before page unloads
      writeWizardStep(state.campaign_id!, state.current_step);

      // EPOCH 59: Also save full state to cache
      WizardCache.write(state.campaign_id!, {
        step: state.current_step,
        leadMethod: state.lead_acquisition_method,
        campaignName: state.campaign_name,
        serviceTier: state.service_tier,
        messages: state.messages,
        sequenceName: state.sequence_name,
        campaignGoal: state.campaignGoal
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.campaign_id, state.current_step, state.messages, state.sequence_name, state.lead_acquisition_method, state.campaign_name, state.service_tier]);

  // Populate state with existing campaign data for edit/view mode
  useEffect(() => {
    if (existingCampaign && (isEditMode || isViewMode)) {
      // Check cache first for instant restoration
      const cachedData = WizardCache.read(existingCampaign.id);
      
      // Reconcile cache with server data
      const reconciledState = reconcileWizardState(existingCampaign, cachedData);
      
      // Only restore step if we haven't already done early restoration
      const shouldRestoreStep = !state.campaign_id || state.campaign_id !== existingCampaign.id;
      // EPOCH 63: Cap max step to 5 (was 6 before Service Tier step removal)
      const restoredStep = shouldRestoreStep
        ? (cachedData?.step || readWizardStep(existingCampaign.id, 5))
        : state.current_step; // Keep existing step if already restored
      
      // EPOCH 59: Enhanced logging to include messages from cache
      console.log('🔄 Restoring campaign state:', {
        campaignId: existingCampaign.id,
        campaignName: existingCampaign.name,
        status: existingCampaign.status,
        restoredStep,
        shouldRestoreStep,
        currentStep: state.current_step,
        isEditMode,
        isViewMode,
        fromCache: !!cachedData,
        reconciledLeadMethod: reconciledState.lead_acquisition_method,
        // EPOCH 59: Log messages restoration
        cachedMessagesCount: reconciledState.messages?.length || 0,
        cachedSequenceName: reconciledState.sequence_name || '(none)'
      });

      setState(prevState => ({
        ...prevState,
        campaign_id: existingCampaign.id, // Add campaign_id for edit mode
        campaign_name: reconciledState.campaign_name || existingCampaign.name || prevState.campaign_name,
        service_tier: reconciledState.service_tier || existingCampaign.service_tier || prevState.service_tier,
        // Use reconciled lead method that considers both cache and server
        lead_acquisition_method: reconciledState.lead_acquisition_method as LeadSourceOrNull,
        lead_lists_ids: existingCampaign.lead_lists_ids || prevState.lead_lists_ids,
        total_estimated_leads: existingCampaign.total_leads || prevState.total_estimated_leads,
        ai_chatbot_enabled: existingCampaign.ai_chatbot_config?.enabled || prevState.ai_chatbot_enabled,
        // Epoch 80: restore campaign goal from DB (cache wins if set)
        campaignGoal: reconciledState.campaignGoal || existingCampaign.campaign_goal as ('product_discovery' | 'lead_generation' | undefined) || prevState.campaignGoal,
        current_step: restoredStep, // Use calculated step
        // EPOCH 59: Restore messages and sequence_name from cache if available
        // Priority: cache > prevState > empty default
        sequence_name: reconciledState.sequence_name || prevState.sequence_name || '',
        messages: reconciledState.messages && reconciledState.messages.length > 0
          ? reconciledState.messages
          : (prevState.messages.length > 0 ? prevState.messages : [])
      }));

      // EPOCH 59: Only fetch sequence data from DB if we don't have messages from cache
      // This prevents overwriting cached messages with stale DB data
      if (existingCampaign.sequence_id && (!reconciledState.messages || reconciledState.messages.length === 0)) {
        console.log('📡 EPOCH59: No messages in cache, fetching from database...');
        loadSequenceData(existingCampaign.sequence_id);
      } else if (reconciledState.messages && reconciledState.messages.length > 0) {
        console.log('✅ EPOCH59: Using cached messages, skipping DB fetch');
      }
    }
  }, [existingCampaign, isEditMode, isViewMode]);

  // Function to load sequence data for existing campaigns
  const loadSequenceData = async (sequenceId: string) => {
    try {
      const { data: sequenceData, error } = await supabase
        .from('campaign_sequences')
        .select('*')
        .eq('id', sequenceId)
        .single();

      if (error) {
        console.error('Error loading sequence data:', error);
        return;
      }

      if (sequenceData) {
        setState(prevState => ({
          ...prevState,
          sequence_name: sequenceData.sequence_name || '',
          messages: sequenceData.messages || []
        }));
      }
    } catch (error) {
      console.error('Error in loadSequenceData:', error);
    }
  };

  // Define wizard steps configuration
  // EPOCH 63: Removed Service Tier step (was Step 2) - now 5 steps total
  // All campaigns default to Platform tier (stored as 'junior_sdr' in DB)
  const wizardSteps: WizardStep[] = [
    {
      step_number: 1,
      title: 'Campaign Name',
      description: 'Give your campaign a memorable name',
      component: 'BasicInformationStep',
      validation_rules: [
        { field: 'campaign_name', required: true, minLength: 3, maxLength: 100 },
        { field: 'campaignGoal', required: true }
      ],
      is_optional: false
    },
    {
      step_number: 2,
      title: 'Lead Method',
      description: 'Choose how to acquire leads',
      component: 'LeadAcquisitionMethodStep',
      validation_rules: [
        { field: 'lead_acquisition_method', required: true }
      ],
      is_optional: false
    },
    {
      step_number: 3,
      title: 'Lead Selection',
      description: 'Configure your target leads',
      component: 'LeadManagementStep',
      validation_rules: [],
      is_optional: false
    },
    {
      step_number: 4,
      title: 'Messages',
      description: 'Create your message sequence',
      component: 'MessageSequenceBuilderStep',
      validation_rules: [
        { field: 'messages', required: true },
        { field: 'sequence_name', required: true, minLength: 3 }
      ],
      is_optional: false
    },
    {
      step_number: 5,
      title: 'Review',
      description: 'Review and confirm',
      component: 'ReviewAndConfirmationStep',
      validation_rules: [],
      is_optional: false
    }
  ];

  const totalSteps = wizardSteps.length;
  const currentStepConfig = wizardSteps.find(step => step.step_number === state.current_step);

  // Check if there are unsaved changes
  const hasUnsavedChanges = Boolean(
    state.campaign_name || 
    state.selected_preset_filters.length > 0 || 
    state.selected_existing_lists.length > 0 ||
    state.messages.length > 0 ||
    state.csv_file
  );

  // Save draft function
  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      // TODO: Implement actual save to database as draft
      const campaignData: Partial<CampaignV2> = {
        name: state.campaign_name,
        service_tier: state.service_tier,
        status: 'draft'
      };
      
      console.log('Saving draft:', campaignData);
      
      // Mock save delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mark as no longer having unsaved changes
      // In a real implementation, you'd update the state to reflect saved status
      
    } catch (error) {
      console.error('Failed to save draft:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [state]);

  // Unsaved changes hook
  useUnsavedChanges({
    hasUnsavedChanges,
    onSave: saveDraft,
    onDiscard: () => {
      // Clear all form data
      setState(prev => ({
        ...prev,
        campaign_name: '',
        selected_preset_filters: [],
        selected_existing_lists: [],
        messages: [],
        csv_file: undefined,
        sequence_name: ''
      }));
    }
  });

  // Note: handleNavigationAttempt was removed as it's no longer used
  // The unsaved changes protection is handled by the useUnsavedChanges hook

  // State update handler with cache writing
  const updateState = useCallback((updates: Partial<CampaignCreationState>) => {
    setState(prev => {
      const newState = {
        ...prev,
        ...updates,
        // Clear validation errors for updated fields
        validation_errors: Object.keys(updates).reduce((_acc, key) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [key]: _, ...rest } = prev.validation_errors;
          return rest;
        }, prev.validation_errors)
      };
      
      // Write critical fields to cache for instant restoration
      if (newState.campaign_id) {
        const cacheUpdate: any = {};

        if ('current_step' in updates) {
          cacheUpdate.step = newState.current_step;
        }
        if ('lead_acquisition_method' in updates) {
          cacheUpdate.leadMethod = newState.lead_acquisition_method;
        }
        if ('campaign_name' in updates) {
          cacheUpdate.campaignName = newState.campaign_name;
        }
        if ('service_tier' in updates) {
          cacheUpdate.serviceTier = newState.service_tier;
        }
        if ('campaignGoal' in updates) {
          cacheUpdate.campaignGoal = newState.campaignGoal;
        }
        // EPOCH 59: Cache messages and sequence_name to prevent data loss on tab switch
        // Also add protection to prevent overwriting good cached data with empty state
        if ('messages' in updates) {
          const newMessages = newState.messages || [];
          const hasContent = newMessages.some(m => m.content && m.content.trim().length > 0);

          // Check existing cache to prevent overwriting good data with empty data
          const existingCache = WizardCache.read(newState.campaign_id);
          const cachedHasContent = existingCache?.messages?.some(m => m.content && m.content.trim().length > 0);

          // EPOCH 59: Don't overwrite cache if:
          // 1. Cache has messages with content AND
          // 2. New data is empty or has no content
          if (cachedHasContent && !hasContent) {
            console.log('📝 EPOCH59: Skipping cache overwrite - preserving existing messages with content');
          } else {
            cacheUpdate.messages = newMessages;
            console.log('📝 EPOCH59: Caching messages:', newMessages.length, 'hasContent:', hasContent);
          }
        }
        if ('sequence_name' in updates) {
          const newSequenceName = newState.sequence_name;
          const existingCache = WizardCache.read(newState.campaign_id);

          // Don't overwrite non-empty sequence name with empty
          if (existingCache?.sequenceName && !newSequenceName) {
            console.log('📝 EPOCH59: Skipping sequence_name overwrite - preserving existing value');
          } else {
            cacheUpdate.sequenceName = newSequenceName;
            console.log('📝 EPOCH59: Caching sequence_name:', newSequenceName);
          }
        }

        if (Object.keys(cacheUpdate).length > 0) {
          WizardCache.write(newState.campaign_id, cacheUpdate);
        }
      }
      
      return newState;
    });
  }, []);

  // Navigation handlers
  const handleNext = useCallback(async () => {
    // Special handling: Create draft campaign after Step 1 to get campaign_id
    // IMPORTANT: Don't create a new draft if we already have a campaign_id (resuming from URL)
    if (state.current_step === 1 && !state.campaign_id && !isEditMode && !isViewMode && !urlCampaignId) {
      setState(prev => ({ ...prev, is_loading: true }));
      
      try {
        // Create draft campaign to get campaign_id for dependent resources
        const draftData = {
          campaign_name: state.campaign_name,
          service_tier: state.service_tier || 'junior_sdr',
          status: 'draft' as const,
          // Minimal fields for draft
          lead_acquisition_method: 'preset_filters' as LeadSource,
          sequence_name: 'Draft Sequence',
          messages: [{
            message_number: 1,
            content: '',
            character_count: 0,
            delay_days: 0
          }],
          total_estimated_leads: 0,
          campaignGoal: state.campaignGoal
        };
        
        const csrContext = isCSR && targetUser ? {
          targetUserId: targetUser.id,
          csrNotes: `CSR creating draft campaign: ${state.campaign_name}`
        } : undefined;
        
        const result = await createCampaign(draftData, csrContext);
        
        if (result.success && result.data?.campaign_id) {
          console.log('Draft campaign created with ID:', result.data.campaign_id);
          setState(prev => ({ 
            ...prev, 
            campaign_id: result.data.campaign_id,
            is_loading: false 
          }));
          
          // Update URL to include campaign_id for reload recovery
          // This ensures the wizard can resume if the page reloads
          updateWizardUrl(result.data.campaign_id);
          
          // Store current step in sessionStorage for recovery
          writeWizardStep(result.data.campaign_id, 2);
        } else {
          throw new Error(result.error || 'Failed to create draft campaign');
        }
      } catch (error) {
        console.error('Error creating draft campaign:', error);
        setState(prev => ({ 
          ...prev, 
          is_loading: false,
          validation_errors: {
            general: ['Failed to initialize campaign. Please try again.']
          }
        }));
        return; // Don't proceed to next step
      }
    }
    
    // Auto-save critical data when moving from specific steps
    if (state.current_step === 3 && state.campaign_id && state.campaign_name) {
      // Save lead acquisition method when leaving Step 3
      console.log('Auto-saving lead acquisition method:', state.lead_acquisition_method);
      try {
        // Pass the full state with the updated field
        await updateCampaign(state.campaign_id, {
          ...state,
          target_audience_type: state.lead_acquisition_method
        });
      } catch (error) {
        console.error('Failed to save lead acquisition method:', error);
      }
    }
    
    if (state.current_step < totalSteps) {
      const nextStep = state.current_step + 1;
      setState(prev => ({ ...prev, current_step: nextStep }));
      // Store current step for recovery after reload
      // Use the campaign_id from state or URL
      const campaignIdToSave = state.campaign_id || urlCampaignId;
      if (campaignIdToSave) {
        writeWizardStep(campaignIdToSave, nextStep);
      }
    }
  }, [state.current_step, state.campaign_id, state.campaign_name, state.service_tier, state.lead_acquisition_method, totalSteps, isEditMode, isViewMode, isCSR, targetUser, urlCampaignId]);

  const handlePrev = useCallback(() => {
    if (state.current_step > 1) {
      const prevStep = state.current_step - 1;
      setState(prev => ({ ...prev, current_step: prevStep }));
      // Store current step for recovery after reload
      // Use the campaign_id from state or URL
      const campaignIdToSave = state.campaign_id || urlCampaignId;
      if (campaignIdToSave) {
        writeWizardStep(campaignIdToSave, prevStep);
      }
    }
  }, [state.current_step, state.campaign_id, urlCampaignId]);

  const handleCancel = useCallback(() => {
    // Clean up sessionStorage when cancelling
    if (state.campaign_id) {
      clearWizardStep(state.campaign_id);
    }
    
    if (hasUnsavedChanges) {
      setPendingNavigation('/campaigns');
      setShowUnsavedModal(true);
      
      window.unsavedChangesCallbacks = {
        onConfirm: () => {
          setShowUnsavedModal(false);
          if (onCancel) {
            onCancel();
          } else {
            navigate('/campaigns');
          }
        },
        onCancel: () => {
          setShowUnsavedModal(false);
          setPendingNavigation(null);
        },
        onSave: async () => {
          await saveDraft();
          setShowUnsavedModal(false);
          if (onCancel) {
            onCancel();
          } else {
            navigate('/campaigns');
          }
        }
      };
    } else {
      if (onCancel) {
        onCancel();
      } else {
        navigate('/campaigns');
      }
    }
  }, [onCancel, navigate, hasUnsavedChanges, saveDraft]);

  // Modal handlers
  const handleModalSave = async () => {
    if (window.unsavedChangesCallbacks?.onSave) {
      await window.unsavedChangesCallbacks.onSave();
    }
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const handleModalDiscard = () => {
    if (window.unsavedChangesCallbacks?.onConfirm) {
      window.unsavedChangesCallbacks.onConfirm();
    }
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const handleModalCancel = () => {
    if (window.unsavedChangesCallbacks?.onCancel) {
      window.unsavedChangesCallbacks.onCancel();
    }
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  // Helper function to get user-friendly field names
  const getFieldDisplayName = (fieldName: string): string => {
    const fieldNameMap: Record<string, string> = {
      'campaign_name': 'Campaign name',
      'sequence_name': 'Sequence name',
      'messages': 'Messages',
      'service_tier': 'Service tier',
      'lead_acquisition_method': 'Lead acquisition method',
      'selected_preset_filters': 'Preset filters',
      'csv_file': 'CSV file',
      'selected_existing_lists': 'Existing lists'
    };
    
    return fieldNameMap[fieldName] || fieldName;
  };

  // Validation
  const validateCurrentStep = useCallback(() => {
    if (!currentStepConfig) return true;
    
    const errors: Record<string, string[]> = {};
    
    currentStepConfig.validation_rules.forEach(rule => {
      const value = state[rule.field];
      const fieldErrors: string[] = [];
      const fieldDisplayName = getFieldDisplayName(rule.field as string);
      
      if (rule.required && (!value || (Array.isArray(value) && value.length === 0))) {
        fieldErrors.push(`${fieldDisplayName} is required`);
      }
      
      if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
        fieldErrors.push(`${fieldDisplayName} must be at least ${rule.minLength} characters`);
      }
      
      if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
        fieldErrors.push(`${fieldDisplayName} must be no more than ${rule.maxLength} characters`);
      }
      
      if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
        fieldErrors.push(`${fieldDisplayName} has invalid format`);
      }
      
      if (rule.custom && typeof rule.custom === 'function') {
        const customError = rule.custom(value);
        if (customError) fieldErrors.push(customError);
      }
      
      if (fieldErrors.length > 0) {
        errors[rule.field as string] = fieldErrors;
      }
    });
    
    if (Object.keys(errors).length > 0) {
      setState(prev => ({ ...prev, validation_errors: errors }));
      return false;
    }
    
    return true;
  }, [currentStepConfig, state]);

  // Save as Draft handler
  const handleSaveAsDraft = useCallback(async () => {
    setState(prev => ({ ...prev, is_loading: true }));
    
    try {
      // Validate current step before saving
      if (!validateCurrentStep()) {
        setState(prev => ({ ...prev, is_loading: false }));
        return;
      }

      // With draft-first pattern, campaign should already exist
      const campaignIdToUpdate = isEditMode && existingCampaign ? existingCampaign.id : state.campaign_id;
      
      if (!campaignIdToUpdate) {
        throw new Error('No campaign to save. Campaign must be created first.');
      }

      // Prepare draft state with minimum required fields
      const draftState = {
        ...state,
        status: 'draft' as const,
        updated_at: new Date().toISOString(),
        // Ensure required fields are set with defaults for drafts
        sequence_name: state.sequence_name || 'Draft Sequence',
        messages: state.messages.length > 0 ? state.messages : [
          {
            message_number: 1,
            content: 'Draft message content',
            character_count: 20,
            delay_days: 0
          }
        ]
      };

      // Update existing campaign as draft
      console.log('Saving campaign as draft:', campaignIdToUpdate);
      const result = await updateCampaign(campaignIdToUpdate, draftState);
      
      if (result.success) {
        console.log('Campaign saved as draft successfully');
        
        // Navigate back to campaigns list with success message
        navigate('/campaigns', { 
          state: { 
            message: `Campaign "${draftState.campaign_name}" saved as draft successfully!`,
            type: 'success' 
          } 
        });
      } else {
        // Handle errors
        setState(prev => ({ 
          ...prev, 
          validation_errors: { 
            general: [result.error || 'Failed to save campaign as draft. Please try again.'] 
          } 
        }));
      }
    } catch (error) {
      console.error('Error saving campaign as draft:', error);
      setState(prev => ({ 
        ...prev, 
        validation_errors: { 
          general: ['Network error. Please check your connection and try again.'] 
        } 
      }));
    } finally {
      setState(prev => ({ ...prev, is_loading: false }));
    }
  }, [state, validateCurrentStep, navigate, isEditMode, existingCampaign, isCSR, targetUser]);

  // Final submission handler
  const handleComplete = useCallback(async () => {
    setState(prev => ({ ...prev, is_loading: true }));
    
    try {
      // Prepare state based on user's submission choice
      const finalStatus = state.submit_for_review ? 'submitted' : 'draft';
      const submissionState = {
        ...state,
        status: finalStatus as const,
        submitted_at: state.submit_for_review ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      };

      let result;
      
      // With draft-first pattern, we always update (never create at this point)
      const campaignIdToUpdate = isEditMode && existingCampaign ? existingCampaign.id : state.campaign_id;
      
      if (!campaignIdToUpdate) {
        throw new Error('No campaign ID found. Campaign must be created first.');
      }
      
      console.log('Updating campaign:', campaignIdToUpdate, 'to status:', finalStatus);
      result = await updateCampaign(campaignIdToUpdate, submissionState);
      
      // With draft-first pattern, filter requests are already created
      
      if (result.success && result.data) {
        console.log('Campaign ' + (isEditMode ? 'updated' : 'created') + ' successfully:', result.data);
        
        // Create a CampaignV2 object for backwards compatibility
        const campaignData: CampaignV2 = {
          id: result.data.campaign_id,
          user_id: existingCampaign?.user_id || '', // Use existing user_id for edits
          name: state.campaign_name,
          service_tier: state.service_tier,
          status: finalStatus as const, // Use the actual status based on user choice
          execution_status: existingCampaign?.execution_status || 'not_started' as const,
          sequence_id: result.data.sequence_id,
          lead_lists_ids: result.data.lead_list_ids || [],
          total_leads: state.total_estimated_leads,
          ai_chatbot_config: state.ai_chatbot_enabled ? state.ai_chatbot_config : undefined,
          created_at: existingCampaign?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          execution_config: existingCampaign?.execution_config || {},
          // Legacy fields for backwards compatibility
          assigned_csr: existingCampaign?.assigned_csr || null,
          target_audience_type: state.lead_acquisition_method,
          target_audience_filter: state.lead_acquisition_method === 'preset_filters' 
            ? JSON.stringify(state.selected_preset_filters) 
            : null,
          target_audience_file_url: existingCampaign?.target_audience_file_url || null,
          submitted_at: existingCampaign?.submitted_at || null,
          started_at: existingCampaign?.started_at || null,
          completed_at: existingCampaign?.completed_at || null,
          dashboard_url: existingCampaign?.dashboard_url || null
        };
        
        // Clean up sessionStorage when campaign is completed
        if (state.campaign_id) {
          clearWizardStep(state.campaign_id);
        }
        
        if (onComplete) {
          onComplete(campaignData);
        } else {
          // Show success message based on submission type and navigate
          const successMessage = state.submit_for_review 
            ? `Campaign "${state.campaign_name}" submitted for review! Your CSR will be notified.`
            : `Campaign "${state.campaign_name}" saved as draft successfully!`;
          
          console.log('Campaign completed! Message:', successMessage);
          navigate('/campaigns', { 
            state: { 
              message: successMessage,
              type: 'success' 
            }
          });
        }
      } else {
        // Handle API error
        console.error('Campaign creation failed:', result.error);
        setState(prev => ({ 
          ...prev, 
          validation_errors: { 
            general: [result.error || 'Failed to create campaign. Please try again.'] 
          } 
        }));
      }
    } catch (error) {
      console.error('Network error during campaign creation:', error);
      setState(prev => ({ 
        ...prev, 
        validation_errors: { 
          general: ['Network error. Please check your connection and try again.'] 
        } 
      }));
    } finally {
      setState(prev => ({ ...prev, is_loading: false }));
    }
  }, [state, onComplete, navigate, isEditMode, existingCampaign, isCSR, targetUser]);

  // Step component renderer
  const renderStepComponent = useCallback(() => {
    const stepProps = {
      state,
      updateState,
      onNext: handleNext,
      onPrev: handlePrev,
      isLoading: state.is_loading,
      errors: state.validation_errors,
      isViewMode,
      isEditMode,
      isFirstCampaign
    };

    // EPOCH 63: Updated step mapping - removed Service Tier (was step 2)
    // Step 1: Campaign Name, Step 2: Lead Method, Step 3: Lead Selection, Step 4: Messages, Step 5: Review
    switch (state.current_step) {
      case 1:
        return <BasicInformationStep {...stepProps} />;
      case 2:
        return <LeadAcquisitionMethodStep {...stepProps} />;
      case 3:
        return <LeadManagementStep {...stepProps} />;
      case 4:
        return <MessageSequenceBuilderStep {...stepProps} />;
      case 5:
        return <ReviewAndConfirmationStep {...stepProps} />;
      default:
        return <div>Invalid step</div>;
    }
  }, [state, updateState, handleNext, handlePrev, isViewMode, isEditMode, isFirstCampaign]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleCancel]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header is provided by AuthenticatedRoute wrapper */}
      
      {/* Progress Header */}
      <WizardProgress 
        currentStep={state.current_step}
        totalSteps={totalSteps}
        stepTitles={wizardSteps.map(step => step.title)}
        isViewMode={isViewMode}
        isEditMode={isEditMode}
      />
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* CSR User Selector - Show for CSRs on first step */}
        {isCSR && state.current_step === 1 && !isEditMode && (
          <div className="mb-8">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4">
                🎭 Create Campaign on Behalf of User
              </h3>
              <p className="text-purple-700 mb-4">
                As a CSR, you can create campaigns for users who need assistance. 
                {targetUser ? ' You are currently acting on behalf of:' : ' Select a user to get started:'}
              </p>
              <CSRUserSelector className="max-w-md" />
              {!targetUser && (
                <p className="mt-4 text-sm text-purple-600">
                  Note: You can also create campaigns for yourself by not selecting a user.
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Step Header */}
        {currentStepConfig && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {currentStepConfig.title}
            </h2>
            <p className="text-lg text-gray-600">
              {currentStepConfig.description}
            </p>
          </div>
        )}
        
        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {renderStepComponent()}
        </div>
        
        {/* Navigation Footer */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={handleCancel}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={state.is_loading}
          >
            {isViewMode ? 'Back to Campaigns' : 'Cancel'}
          </button>
          
          {!isViewMode && (
            <div className="flex space-x-3">
              {/* Save as Draft Button - Only show on steps 4+ */}
              {state.current_step >= 4 && (
                <button
                  onClick={handleSaveAsDraft}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  disabled={state.is_loading}
                >
                  {state.is_loading ? 'Saving...' : 'Save as Draft'}
                </button>
              )}
              
              {/* Previous Button */}
              {state.current_step > 1 && (
                <button
                  onClick={handlePrev}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={state.is_loading}
                >
                  Previous
                </button>
              )}
              
              {/* Next/Submit Button */}
              {state.current_step < totalSteps ? (
                <button
                  onClick={() => {
                    if (validateCurrentStep()) {
                      handleNext();
                    }
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={state.is_loading}
                >
                  {state.is_loading ? 'Loading...' : 'Next'}
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  className={`px-8 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                    state.submit_for_review 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  disabled={state.is_loading}
                >
                  {state.is_loading 
                    ? (state.submit_for_review ? 'Submitting for Review...' : 'Saving Draft...') 
                    : (state.submit_for_review ? 'Submit for Review' : 'Save as Draft')
                  }
                </button>
              )}
            </div>
          )}
          
          {isViewMode && (
            <div className="flex space-x-3">
              {/* Previous Button */}
              {state.current_step > 1 && (
                <button
                  onClick={handlePrev}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
              )}
              
              {/* Next Button or End Message */}
              {state.current_step < totalSteps ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Next
                </button>
              ) : (
                <span className="text-sm text-gray-500">End of campaign details</span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Global error display */}
      {state.validation_errors.general && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-lg">
          {state.validation_errors.general.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}
      
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        message="You have unsaved campaign changes. What would you like to do?"
        showSaveOption={true}
        onSave={handleModalSave}
        onDiscard={handleModalDiscard}
        onCancel={handleModalCancel}
        isSaving={isSaving}
      />
    </div>
  );
};

export default CampaignCreationWizard;